import React from "react";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Backend (PlayReportManager) is the primary source via backendMeta { colorHex, label, sub }.
// pointColor() and scoreMeaning() are retained as local fallbacks.

const THRESHOLDS = [
  { min: 90, label: "Outstanding", sub: "Peak cognitive performance",  color: "#4CAF50" },
  { min: 75, label: "Excellent",   sub: "Strong mental fitness",       color: "#4CAF50" },
  { min: 60, label: "Good",        sub: "Above average performance",   color: "#FF9800" },
  { min: 50, label: "Fair",        sub: "Room for improvement",        color: "#FF9800" },
  { min:  1, label: "Needs Work",  sub: "Increase daily practice",     color: "#f44336" },
];

const WELLNESS_THRESHOLDS = [
  { min: 90, label: "Outstanding", sub: "Excellent brain wellness",    color: "#4CAF50" },
  { min: 75, label: "Excellent",   sub: "Strong mental wellbeing",     color: "#4CAF50" },
  { min: 60, label: "Good",        sub: "Above average engagement",    color: "#FF9800" },
  { min: 50, label: "Fair",        sub: "Room to grow",                color: "#FF9800" },
  { min:  1, label: "Needs Work",  sub: "Increase daily activity",     color: "#f44336" },
];

function pointColor(score) {
  const t = THRESHOLDS.find((t) => score >= t.min);
  return t ? t.color : "#bdbdbd";
}

function scoreMeaning(score, wellness) {
  const list = wellness ? WELLNESS_THRESHOLDS : THRESHOLDS;
  const t = list.find((t) => score >= t.min);
  return t ? { label: t.label, sub: t.sub } : { label: "No Data", sub: "" };
}

function scoreDisplay(score, backendMeta, wellness) {
  if (backendMeta?.colorHex || backendMeta?.ColorHex) {
    return {
      color: backendMeta.colorHex || backendMeta.ColorHex,
      label: backendMeta.label || backendMeta.Label || "",
      sub:   backendMeta.sub   || backendMeta.Sub   || "",
    };
  }
  const meaning = scoreMeaning(score, wellness);
  return { color: pointColor(score), label: meaning.label, sub: meaning.sub };
}

export default function CognitiveFitnessProgress({ labels, scores, meta, loading, report, wellness }) {
  const points = labels.map((label, i) => ({
    label,
    score: scores[i] ?? 0,
    meta: meta?.[i] ?? null,
  }));
  const hasData = points.length > 0 && points.some((p) => p.score > 0);

  return (
    <Card sx={{ width: "100%", overflow: "visible" }}>
      <MDBox display="flex" alignItems="center" px={3} pt={3} pb={1} gap={1}>
        <Icon sx={{ color: "info.main", fontSize: "1.4rem !important" }}>psychology</Icon>
        <MDTypography variant="h6">{wellness ? "Brain Wellness Score" : "Cognitive Fitness Score"}</MDTypography>
        {report?.name && (
          <MDTypography variant="caption" color="text" ml={1}>
            — {report.name}
          </MDTypography>
        )}
      </MDBox>

      <MDBox px={4} pb={4} pt={2}>
        {loading ? (
          <MDBox display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </MDBox>
        ) : !hasData ? (
          <MDTypography variant="body2" color="secondary" py={2}>
            No data for this period. Click Load Reports.
          </MDTypography>
        ) : (
          <MDBox
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: 4,
              mb: 3,
            }}
          >
            {/* Connecting line */}
            <MDBox
              sx={{
                position: "absolute",
                top: "50%",
                left: "calc(12.5%)",
                right: "calc(12.5%)",
                height: "3px",
                background: "linear-gradient(to right, #bdbdbd, #1976d2)",
                transform: "translateY(-50%)",
                zIndex: 0,
              }}
            />

            {points.map((pt, idx) => {
              const { color, label, sub } = scoreDisplay(pt.score, pt.meta, wellness);
              const isLast = idx === points.length - 1;
              return (
                <MDBox
                  key={idx}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    zIndex: 1,
                    flex: 1,
                  }}
                >
                  <MDTypography
                    variant="h4"
                    fontWeight="bold"
                    sx={{ color, mb: 0.5, lineHeight: 1.2 }}
                  >
                    {pt.score > 0 ? pt.score.toFixed(1) : "—"}
                  </MDTypography>

                  <MDBox
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: "50%",
                      background: isLast
                        ? `radial-gradient(circle, ${color} 60%, white 100%)`
                        : "white",
                      border: `3px solid ${color}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: isLast ? `0 0 0 4px ${color}33` : `0 2px 6px rgba(0,0,0,0.15)`,
                      transition: "transform 0.2s",
                      "&:hover": { transform: "scale(1.15)", cursor: "default" },
                    }}
                  >
                    <Icon sx={{ fontSize: "1.3rem !important", color: isLast ? "white" : color }}>
                      psychology
                    </Icon>
                  </MDBox>

                  <MDTypography variant="caption" color="text" sx={{ mt: 0.5, whiteSpace: "nowrap" }}>
                    {pt.label}
                  </MDTypography>

                  {pt.score > 0 && label && (
                    <MDBox textAlign="center" mt={0.5}>
                      <MDTypography variant="button" fontWeight="bold" sx={{ color, display: "block", lineHeight: 1.3 }}>
                        {label}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" sx={{ display: "block", lineHeight: 1.2 }}>
                        {sub}
                      </MDTypography>
                    </MDBox>
                  )}
                </MDBox>
              );
            })}
          </MDBox>
        )}
      </MDBox>
    </Card>
  );
}
